import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import CookiesNotification from './CookiesNotification';
import Notification from './Notification';
import Logo from './Logo';
import Navigation from './Navigation';
import Search from './Search';
import Cart from '../Selection/Cart';
import Select from '../Elements/Select/Select';
import APLACElogo from './APLACE-logo.svg';
import { translate, prefix } from '../../utils/string';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset } from '../../utils/dom';
import { removeNotification, openCart, openMenu, expandMenuItem, clearFilter, resetFilter, changeLanguage, goToPage, changeDevice } from '../../actions/environment';
import { clientInit, changeCountry, changeAddressInput } from '../../actions/checkout';
import s from './Header.scss';

const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };

class Header extends Component {
	constructor(props) {
		super(props);

		this.state = {
			location: false,
			menuPopupHeight: 'auto',
			menuClicked: false,
		};

		this.menuOffset = null;
		this.firstScroll = false;

		this.headerElement = React.createRef();
		this.linksElement = React.createRef();
		this.cartElement = React.createRef();
		this.languageCountryElement = React.createRef();
	}

	componentDidMount() {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');
			let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
			let device = media[`w_${currentWidth}`];

			this.props.clientInit();
			this.props.changeDevice(device);

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!this.headerElement || !this.linksElement || !this.cartElement) {
					setTimeout(initiateSiteListeners, 200);
				}

				const startTop = window.pageYOffset || document.documentElement.scrollTop;
				const logoAspectRatio = 0.168553459;
				const headerHeight = (window.innerWidth - 40) * logoAspectRatio;
				this.menuOffset = offset(this.headerElement.current).top + headerHeight;
				const menuOffsetWindowTop = startTop > this.menuOffset ? 0 : this.menuOffset - startTop;

				if (this.state.menuPopupHeight === 'auto') {
					this.setState({
						menuPopupHeight: window.innerHeight - menuOffsetWindowTop - 48,
					});
				}

				const resizeEvent = () => {
					const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);

					// Switch between mobile, table and desktop.
					if (currentWidth !== updatedWidth) {
						device = media[`w_${updatedWidth}`];
						this.props.changeDevice(device);
						currentWidth = updatedWidth;
					}
				};

				addEvent(window, 'resize.logoHeader', () => {
					window.requestAnimationFrame(resizeEvent);
				});

				addEvent(document.querySelector('body'), 'click.cartClose', (e) => {
					if (this.cartElement.current && !this.cartElement.current.contains(e.target) && !this.headerElement.current.contains(e.target) && this.props.properties.openCart === 1) {
						e.preventDefault();
						e.stopPropagation();
						this.props.openCart(false);
					}
					if (this.languageCountryElement.current && !this.languageCountryElement.current.contains(e.target) && this.state.location) {
						e.preventDefault();
						e.stopPropagation();
						this.setState({ location: false });
					}
				});
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.querySelector('body'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && this.props.checkout.klarnaUnloaded && $link.getAttribute('href').indexOf('#') !== 0) {
					e.preventDefault();
					e.stopPropagation();
					window.location = $link.href;
					// Force full page reload to clear Klarna on next page click.
				} else if ($link && hasClass($link, 'linkCapture') && !/(http|mailto|tel)/.test($link.getAttribute('href'))) {
					e.preventDefault();
					this.props.goToPage(e.target.getAttribute('href'));
				}
			});

			addEvent(window, 'keydown.cartSearchClose', (e) => {
				if (e.key === 'Escape') {
					if (this.props.properties.openCart === 1) this.props.openCart(false);
					if (this.state.location === true) this.setState({ location: false });
				}
			});
		}
	}

	componentWillUnmount() {
		removeEvent(document.querySelector('body'), 'click.cartClose');
		removeEvent(window, 'keydown.cartSearchClose');
		removeEvent(window, 'scroll.logoHeader');
		removeEvent(window, 'resize.logoHeader');
	}

	notificationRemovedHandler = () => {
		setTimeout(() => {
			const top = window.pageYOffset || document.documentElement.scrollTop;
			const logoAspectRatio = 0.168553459;
			const logoWidth = window.innerWidth - 40;
			this.menuOffset = offset(this.headerElement.current).top + (logoWidth * logoAspectRatio) + 29;
			const menuOffsetWindowTop = top > this.menuOffset ? 0 : this.menuOffset - top;

			this.setState({
				menuPopupHeight: window.innerHeight - menuOffsetWindowTop - 48,
			});
		}, 350);
	}

	openCartHandler = (e) => {
		if (this.props.properties.openCart === 0) {
			e.preventDefault();

			this.setState(() => {
				const top = window.pageYOffset || document.documentElement.scrollTop;
				const menuOffsetWindowTop = top > this.menuOffset ? 0 : this.menuOffset - top;

				return { menuPopupHeight: window.innerHeight - menuOffsetWindowTop - 48 };
			});

			if (this.props.navigation.open) this.props.openMenu();
			this.props.openCart();
		} else if (this.props.checkout.klarnaUnloaded) {
			e.preventDefault();
			window.location = `${window.location.origin}${prefix(this.props.setup, 'selection')}`;
		}
	}

	changeLanguage = (e) => {
		const changeTo = e.currentTarget.dataset.language;
		this.props.changeLanguage(changeTo);
		this.setState({
			location: false,
		});
	}

	openLocation = (e) => {
		e.preventDefault();

		this.setState((prevState) => ({ location: !prevState.location }));
	}

	openMobileMenuHandler = (e) => {
		e.preventDefault();

		const top = window.pageYOffset || document.documentElement.scrollTop;
		this.setState(() => {
			const menuOffsetWindowTop = top > this.menuOffset ? 0 : this.menuOffset - top;
			return ({
				menuPopupHeight: window.innerHeight - menuOffsetWindowTop - 48,
			});
		});

		if (this.props.properties.openCart === 1) this.props.openCart(false);
		this.props.openMenu();
	}

	expandMenuHandler = (e) => {
		const index = Array.from(e.currentTarget.closest('ul').children).indexOf(e.currentTarget.closest('li'));

		if (this.props.navigation.expandedMenuItem !== index && window.innerWidth < 750) {
			e.preventDefault();
			this.props.expandMenuItem(index);
		}
	}

	resetFilterHandler = () => {
		this.props.resetFilter();
		this.setState({
			menuClicked: true,
		});
		setTimeout(() => {
			this.setState({
				menuClicked: false,
			});
		}, 400);
	}

	render() {
		const { properties, navigation, brands, setup, header, pagePaths, favourites, checkout, location, productCache, brandList } = this.props;
		const { menuClicked } = this.state;
		if (properties.siteInitiated === false || properties.siteClosed === true) return null;

		const openCartStates = [null, 'is-showingCart__entering', 'is-showingCart__exiting'];

		const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: `${setup.countries[country].name}${(!setup.countries[country].shipTo ? ' *' : '')}`, disabled: !setup.countries[country].shipTo }] : [...acc]), []);
		const onCountryChange = (id, value) => {
			this.props.changeCountry(value);
			this.props.changeAddressInput('address', 'country', value);
			this.props.changeAddressInput('shippingAddress', 'country', value);
			this.setState({
				location: false,
			});
		};

		let searchReturn = prefix(setup, '');
		if (location.prev && location.prev.type !== 'SEARCH' && location.prev.pathname) searchReturn = location.prev.pathname;

		return (
			<>
				<Notification type="campaign" content={header.campaign} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				<CookiesNotification type="cookies" content={header.cookies} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				<Notification type="popup" content={header.popup} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				{ setup.unableToSendToCountry ? <Notification type="error" content={[{ name: 'unableToSendToCountry', text: setup.statics.unableToSendToCountry }]} setup={setup} /> : null }
				{ menuClicked ? <div className={s.header_clickHoverRemove} /> : null }
				<header className={[s.header, openCartStates[properties.openCart]].join(' ')} ref={this.headerElement}>
					<Logo headerElement={this.headerElement} linksElement={this.linksElement} setup={setup} cartIsOpen={properties.openCart} menuIsOpen={navigation.open} />
					<div className={s.header_fixedContainer}>
						<div className={s.header_links} ref={this.linksElement}>
							<span className={s.header_links_byline}>{translate(setup, header.byline)}</span>
							<Link className={s.header_links_stickyLogo} to={prefix(setup, '')}><img src={APLACElogo} alt="APLACE" /></Link>
							<Navigation navigation={navigation} brands={brands} brandList={brandList} setup={setup} menuPopupHeight={this.state.menuPopupHeight} campaignMenu={header.campaignMenu} productCache={productCache} openMenuHandler={this.openMobileMenuHandler} expandMenuHandler={this.expandMenuHandler} resetFilterHandler={this.resetFilterHandler} />
							<div className={s.functions}>
								<div className={[s.functions_location, ...(this.state.location ? [s.functions_location__open] : [])].join(' ')} ref={this.languageCountryElement}>
									<a className={s.functions_location_title} href="#openLocation" onClick={this.openLocation}><span className="a-headerSettings">{ setup.language.toUpperCase() } / { setup.countries[setup.country].currency }</span></a>
									<div className={s.functions_location_dropdown}>
										<div className={s.functions_location_dropdown_language}>
											{
												Object.keys(setup.languages).map((lang) => (
													<div key={lang} className={[s.functions_location_dropdown_language_link, ...(lang === setup.language ? [s.functions_location_dropdown_language_link__selected] : [])].join(' ')}>
														<Link to={`/${lang}/${pagePaths && pagePaths[lang] ? pagePaths[lang] : ''}`} onClick={this.changeLanguage} data-language={lang}>{ setup.languages[lang].name }</Link><span />
													</div>
												))
											}
										</div>
										<div className={s.functions_location_dropdown_country}>
											<Select id="header-country" selected={setup.country} options={countries} inputChange={onCountryChange} />
										</div>
										{ setup.unableToSendToCountry ? <span className={s.functions_location_dropdown_shipTo}>* {translate(setup, 'notShippingTo')}</span> : null }
									</div>
								</div>
								<Link className={s.functions_search} to={location.type === 'SEARCH' ? searchReturn : prefix(setup, 'search')}>
									<svg xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21">
										<g fill="none" fillRule="evenodd" stroke="#000" strokeWidth="1.3" transform="translate(0 1)">
											<circle cx="13" cy="8" r="8" />
											<path strokeLinecap="square" d="M6.5,13.5 L0.5,19.5" />
										</g>
									</svg>
								</Link>
								<Link className={s.functions_favourites} to={prefix(setup, 'favourites')}>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="22" viewBox="0 0 24 22">
										<path fill={favourites.length > 0 ? '#663886' : 'none'} stroke="#000" strokeWidth="1.3" d="M82.3607178,44.9685735 C89.1964111,38.2322454 85.5537109,32.7345647 81.5004883,32.0919866 C78.7983398,31.6636012 76.7208802,32.7314723 75.0893698,35.2955999 C73.4578594,32.7314723 71.3803998,31.6636012 68.6782514,32.0919866 C64.6250287,32.7345647 60.9823285,38.2322454 67.8180219,44.9685735 C70.9248433,48.0302334 73.289046,50.256918 75.0893698,51.6486272 C76.8896936,50.256918 79.2538964,48.0302334 82.3607178,44.9685735 Z" transform="translate(-63 -31)" />
									</svg>
								</Link>
								<Link className={s.functions_selection} to={prefix(setup, 'selection')} onClick={this.openCartHandler}>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="22" viewBox="0 0 24 22">
										<g fill="none" stroke="#000" strokeWidth="1.3">
											<rect fill={(checkout.selection.items && checkout.selection.items.length > 0 ? '#000' : 'none')} width="22.7" height="16.7" x=".65" y="4.65" />
											<path strokeLinecap="square" d="M16.3,6.3 L16.3,1 L7.7,1 L7.7,6.3 L7.7,6.3" />
										</g>
									</svg>
								</Link>
							</div>
						</div>
					</div>
				</header>
				{ location.type === 'SEARCH' ? <div className={s.searchScreen} /> : null }
				<div className={[s.search, ...(location.type === 'SEARCH' ? [s.search__active] : [])].join(' ')}>
					{
						location.type === 'SEARCH' ? <Search /> : null
					}
				</div>
				<div className={s.cart} ref={this.cartElement}>
					<div className={s.cart_inner}>
						<Cart maxHeight={this.state.menuPopupHeight} isNavigation />
					</div>
				</div>
			</>
		);
	}
}

const mapState = ({ environment, checkout, location, productCache, listing }) => {
	const { properties, navigation, brands, setup, header, pagePaths, favourites } = environment;
	return { properties, navigation, brands, setup, header, pagePaths, favourites, checkout, location, productCache, brandList: listing.brands };
};

export default hot(module)(connect(mapState, { removeNotification, openCart, openMenu, expandMenuItem, clearFilter, resetFilter, changeCountry, changeAddressInput, changeLanguage, goToPage, clientInit, changeDevice })(Header));
